import React, { useMemo, useState } from 'react'
import moment from 'moment'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import CustomButton from '../../../shared/CustomButton'
import CustomTable from '../../../shared/CustomTable'
import { fetchData } from '../../../helpers/fetchData'
import { convertPreAuthClinicalData } from '../../../constants/EmrContants'
import CheckPermission from '../../../hooks/CheckPermission'

const ManualApprovalItemsModal = ({
  emrData,
  visitData,
  onClose,
  onUpdated,
  selectedPatientData
}) => {
  const { hasPermission } = CheckPermission()
  const canUpdateEmr = hasPermission('approval_management', 'update') || hasPermission('emr', 'update')
  const { t } = useTranslation()
  const auth = useSelector(state => state.auth)
  const selectedVisitData = useSelector(state => state.visits?.selectedVisitData)
  const [isSaving, setIsSaving] = useState(false)
  const [selectedApprovalItemIndexes, setSelectedApprovalItemIndexes] =
    useState([])

  const isApprovalRequired = item =>
    item?.is_approval === true || item?.is_approval === 'true'

  const isApprovalNotSent = item =>
    item?.is_approval_sent === false ||
    item?.is_approval_sent === 'false' ||
    item?.is_approval_sent === null ||
    item?.is_approval_sent === undefined ||
    item?.is_approval_sent === ''

  const getApprovalStatus = item =>
    String(
      item?.approval_status ||
        item?.approvalStatus ||
        item?.approval_request_status ||
        item?.status ||
        ''
    ).toLowerCase()

  const values = useMemo(
    () => ({
      accident: emrData?.accident || {},
      careTeamsArray: emrData?.careTeamsArr || [],
      chiefComplaints: emrData?.medical_assessments?.chief_complaints || {
        category: 'chief-complaint',
        codeText: '',
        code: '',
        valueUnit: '',
        value: ''
      },
      diagnosisArray: emrData?.diagnosisArr || [],
      is_accident: emrData?.is_accident || false,
      items: emrData?.items || [],
      supportingInfos: emrData?.supportingInfos || [],
      vitalSigns: emrData?.vital_signs || []
    }),
    [emrData]
  )

  const pendingApprovalItems = useMemo(
    () =>
      (values?.items || [])
        .map((item, index) => ({ ...item, __approvalIndex: index }))
        .filter(item => {
          return (
            isApprovalRequired(item) &&
            isApprovalNotSent(item) &&
            getApprovalStatus(item) === 'pending'
          )
        }),
    [values?.items]
  )

  const pendingIndexes = pendingApprovalItems.map(
    item => item.__approvalIndex
  )
  const allSelected =
    pendingApprovalItems.length > 0 &&
    pendingIndexes.every(index => selectedApprovalItemIndexes.includes(index))

  const toggleSelectAll = () => {
    setSelectedApprovalItemIndexes(allSelected ? [] : pendingIndexes)
  }

  const toggleSelectedItem = index => {
    setSelectedApprovalItemIndexes(prev =>
      prev.includes(index)
        ? prev.filter(itemIndex => itemIndex !== index)
        : [...prev, index]
    )
  }

  const getInsurancePlanId = sourceEmrData =>
    sourceEmrData?.patient?.selected_plan?.insurance_plan_id ||
    sourceEmrData?.patient?.insurance_plans?.[0]?.insurance_plan_id ||
    selectedPatientData?.selected_plan?.insurance_plan_id ||
    visitData?.patient?.selected_plan?.insurance_plan_id ||
    selectedVisitData?.patient?.selected_plan?.insurance_plan_id ||
    selectedPatientData?.insurance_plans?.[0]?.insurance_plan_id ||
    visitData?.patient?.insurance_plans?.[0]?.insurance_plan_id ||
    selectedVisitData?.patient?.insurance_plans?.[0]?.insurance_plan_id ||
    ''

  const currentDiagnosisReferences =
    values?.diagnosisArray?.map((_, index) => index + 1) || []

  const buildRequestItems = selectedIndexSet =>
    (values?.items || [])
      .map((item, index) => ({ ...item, __approvalIndex: index }))
      .filter(item => {
        return (
          selectedIndexSet.has(item.__approvalIndex) &&
          isApprovalRequired(item) &&
          isApprovalNotSent(item) &&
          getApprovalStatus(item) === 'pending'
        )
      })
      .map(item => {
        const itemType =
          item?.selected_category || item?.type || item?.item_type
        const validType =
          itemType && itemType.length < 50 && !itemType.match(/^[0-9a-f]{24}$/i)
            ? itemType
            : 'service-codes'

        return {
          __approvalIndex: item.__approvalIndex,
          _item_id: item?.item_id || null,
          item_tp_ref_no: item?.item_id || item?.id || null,
          source: item?.source || null,
          type: validType,
          supergit_master_code:
            item?.source === 'sfda' || item?.source === 'provider_pharmacy'
              ? item?.supergit_master_code || item?.master_code || null
              : item?.supergit_master_code || null,
          standard_code: item?.standard_code || '',
          standard_description:
            item?.standard_description || item?.item_name || '',
          non_standard_code: item?.is_sfda
            ? item?.non_standard_code || null
            : item?.agreed_code || item?.non_standard_code || null,
          non_standard_description: item?.is_sfda
            ? item?.non_standard_description || null
            : item?.agreed_description ||
              item?.non_standard_description ||
              null,
          medication_selection_reason:
            validType === 'medication-codes'
              ? item?.medication_selection_reason || 'Generic'
              : null,
          prescribed_medication:
            validType === 'medication-codes' && item?.standard_code
              ? String(item?.standard_code)
              : null,
          body_site: item?.body_site || null,
          sub_site: item?.sub_site || '',
          maternity: item?.maternity || false,
          service_start_date: item?.service_start_date
            ? moment(item?.service_start_date).format('YYYY-MM-DD')
            : '',
          service_end_date: item?.service_end_date
            ? moment(item?.service_end_date).format('YYYY-MM-DD')
            : '',
          supporting_info: Array.isArray(item?.supporting_info)
            ? [...item.supporting_info, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14]
            : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14],
          diagnosis: currentDiagnosisReferences,
          care_teams: Array.isArray(item?.care_teams) ? item.care_teams : [1],
          is_package: item?.is_package === 'true' || item?.is_package === true,
          quantity: Number(item?.quantity) || 1,
          code: item?.standard_code || '',
          unit_price: Number(item?.unit_price) || 0,
          tax_amount: Number(item?.tax_amount) || 0,
          factor: Number(item?.factor) || 1,
          net: parseFloat(item?.net || 0),
          discount_amount: (() => {
            const quantity = Number(item?.quantity) || 1
            const unitPrice = Number(item?.unit_price) || 0
            const factor = Number(item?.factor) || 1
            const discountPercentage = Number(item?.discount_amount) || 0
            return (quantity * unitPrice * factor * discountPercentage) / 100
          })(),
          patient_share: Number(item?.patient_share) || 0,
          patient_share_amount:
            Number(item?.patientShareAmount || item?.patient_share_amount) || 0,
          payer_share: Number(item?.payer_share) || 0,
          days_supply: Number(item?.days_supply) || 1
        }
      })

  const isReferralPreAuth = Boolean(
    visitData?.referral || selectedVisitData?.referral
  )
  const referralSupportingInfo = {
    category: 'reason-for-visit',
    code: 'referral',
    code_text: 'Reason for visit'
  }

  const buildSupportingInfo = (type, extraItems = []) => {
    const resolvedExtraItems = isReferralPreAuth
      ? [...extraItems, referralSupportingInfo]
      : extraItems
    const requiredCategoriesByType = {
      professional: [
        'chief-complaint',
        'vital-sign-weight',
        'vital-sign-diastolic',
        'vital-sign-height',
        'vital-sign-systolic',
        'treatment-plan',
        'history-of-present-illness',
        'patient-history',
        'physical-examination',
        'pulse',
        'oxygen-saturation',
        'temperature',
        'respiratory-rate',
        'investigation-result'
      ],
      institutional: [
        'chief-complaint',
        'vital-sign-weight',
        'vital-sign-diastolic',
        'vital-sign-height',
        'vital-sign-systolic',
        'treatment-plan',
        'history-of-present-illness',
        'patient-history',
        'physical-examination',
        'estimated-Length-of-Stay',
        'pulse',
        'oxygen-saturation',
        'temperature',
        'respiratory-rate',
        'investigation-result'
      ],
      oral: [
        'chief-complaint',
        'treatment-plan',
        'history-of-present-illness',
        'patient-history',
        'physical-examination',
        'investigation-result'
      ],
      vision: [
        'treatment-plan',
        'history-of-present-illness',
        'patient-history',
        'physical-examination',
        'investigation-result'
      ],
      pharmacy: [
        'treatment-plan',
        'history-of-present-illness',
        'patient-history',
        'physical-examination',
        'investigation-result'
      ]
    }
    const required = requiredCategoriesByType[type] || []
    const getSuppInfo = cat =>
      values?.supportingInfos?.find(item => item.category === cat)
    const additionalSupportInfos = [
      'treatment-plan',
      'history-of-present-illness',
      'patient-history',
      'physical-examination'
    ].map(category => ({
      category,
      code: getSuppInfo(category)?.code || null,
      code_text: getSuppInfo(category)?.code_text || null,
      start_date: getSuppInfo(category)?.start_date || null,
      end_date: getSuppInfo(category)?.end_date || null,
      value: getSuppInfo(category)?.value || 'none',
      unit: getSuppInfo(category)?.unit || null,
      reason: getSuppInfo(category)?.reason || null,
      image_content_type: getSuppInfo(category)?.image_content_type || null,
      image_title: getSuppInfo(category)?.image_title || null,
      image_creation_date: getSuppInfo(category)?.image_creation_date || null,
      image_value: getSuppInfo(category)?.image_value || null,
      image_minio_path: getSuppInfo(category)?.image_minio_path || null
    }))
    additionalSupportInfos.push({
      category: 'investigation-result',
      code: 'NA',
      code_text: t('Not applicable or Other'),
      start_date: null,
      end_date: null,
      value: null,
      unit: null,
      reason: null,
      image_content_type: '',
      image_title: '',
      image_creation_date: moment().toISOString(),
      image_value: '',
      image_minio_path: null
    })

    const allowedCategories = new Set([
      ...required,
      ...resolvedExtraItems.map(item => item.category)
    ])
    const emrCreationDate = emrData?.created_at
      ? moment(emrData.created_at).format('YYYY-MM-DD')
      : moment().format('YYYY-MM-DD')
    const converted = convertPreAuthClinicalData(
      [
        ...(values?.supportingInfos || []),
        values?.chiefComplaints,
        ...(values?.vitalSigns || []),
        ...additionalSupportInfos.filter(info => required.includes(info.category))
      ],
      emrCreationDate
    ).map(item => {
      if (
        item.category === 'vital-sign-height' &&
        (item.value === '' || item.value === null || item.value === undefined)
      ) {
        return { ...item, value: '170' }
      }
      if (
        item.category === 'vital-sign-weight' &&
        (item.value === '' || item.value === null || item.value === undefined)
      ) {
        return { ...item, value: '70' }
      }
      return item
    })
    const seen = new Set()
    const deduped = []
    for (const item of converted) {
      if (!seen.has(item.category)) {
        seen.add(item.category)
        deduped.push(item)
      }
    }
    return [
      ...deduped.filter(item => allowedCategories.has(item.category)),
      ...resolvedExtraItems
    ]
  }

  const getPayloadType = () => {
    const roleDept = auth?.roleData?.department?.name_en?.toLowerCase()
    const practitionerDept =
      visitData?.practitioner?.department?.name_en?.toLowerCase()
    const visitDept = visitData?.department?.name_en?.toLowerCase()
    const isOphthalmologyDept =
      roleDept === 'ophthalmology' ||
      practitionerDept === 'ophthalmology' ||
      visitDept === 'ophthalmology'
    const isDentalDept = [roleDept, practitionerDept, visitDept].some(dept =>
      ['dental', 'dentistry', 'oral and dental'].includes(dept)
    )
    const isInPatientVisit =
      visitData?.visit_category === 'in_patient' ||
      selectedVisitData?.visit_category === 'in_patient'

    if (isOphthalmologyDept) return 'vision'
    if (isDentalDept) return 'oral'
    if (isInPatientVisit) return 'institutional'
    return 'professional'
  }

  const sendItemsPreAuth = async (type, items, basePayload, supportingInfo) => {
    const response = await fetchData('POST', '/v3/RCM/sendPreAuth', {
      ...basePayload,
      type,
      supporting_info: supportingInfo,
      items,
      encounter: null
    })
    return Boolean(response)
  }

  const markItemsSent = async (resolvedEmrId, sentItems) => {
    const itemIds = sentItems.map(item => item._item_id).filter(Boolean)
    if (!itemIds.length) return
    await fetchData('POST', '/v3/emr/updateItemsField', {
      emr_id: resolvedEmrId,
      visit_id: visitData?.visit_id || selectedVisitData?.visit_id,
      patient_id: String(
        selectedPatientData?.patient_id ||
          emrData?.patient?.patient_id ||
          visitData?.patient?.patient_id ||
          ''
      ),
      item_ids: itemIds
    })
  }

  const handleSendApproval = async () => {
    if (!canUpdateEmr) return
    const resolvedEmrId = emrData?.emr_id || emrData?.id
    if (!resolvedEmrId) {
      toast.error(t('EMR ID is required'))
      return
    }
    if (!selectedApprovalItemIndexes.length) {
      toast.error(t('Select at least one pending approval item.'))
      return
    }

    const insurancePlanId = getInsurancePlanId(emrData)
    if (!insurancePlanId) {
      toast.error(t('Insurance plan is required to send approval.'))
      return
    }

    const selectedIndexSet = new Set(selectedApprovalItemIndexes.map(Number))
    const itemsObj = buildRequestItems(selectedIndexSet)
    if (!itemsObj.length) {
      toast.error(t('No pending approval items found for this EMR.'))
      return
    }

    const diagnosisObj = values?.diagnosisArray?.map(data => ({
      type: data?.type,
      on_admission:
        data?.on_admission === 'false' || data?.on_admission === false
          ? false
          : true,
      code: data?.codeId || data?.code,
      description: data?.codeDescription || data?.description
    }))
    const basePayload = {
      is_edit_request: false,
      ref_transaction_id: null,
      is_followup_req: false,
      prv_transaction_id: '',
      patient_id:
        emrData?.patient?.rcm_ref ||
        selectedPatientData?.rcm_ref ||
        visitData?.patient?.rcm_ref ||
        selectedVisitData?.patient?.rcm_ref ||
        '',
      insurance_plan_id: insurancePlanId,
      is_new_born: false,
      is_referral: false,
      date_ordered: moment().format('YYYY-MM-DD'),
      priority: 'stat',
      subtype: 'op',
      offline_verification_id: null,
      offline_verification_date: null,
      verification_resp_ident_value: null,
      verification_resp_ident_url: null,
      physicians_id: [
        parseInt(
          auth?.roleData?.rcm_ref || visitData?.practitioner?.rcm_ref || 0
        )
      ],
      diagnosis: diagnosisObj || [],
      is_accident: values?.is_accident || false,
      accident_info: {
        street_name: values?.accident?.street || '',
        city: values?.accident?.city || '',
        state: values?.accident?.province || '',
        country: values?.accident?.country || '',
        date: values?.accident?.date
          ? moment(values?.accident?.date).format('YYYY-MM-DD')
          : ''
      },
      tp_ref_no: Number(resolvedEmrId),
      vision_prescription: null,
      is_vision_prescription: false
    }

    setIsSaving(true)
    try {
      let sentAnyApproval = false
      const pharmacyItems =
        itemsObj?.filter(item => item?.type === 'medication-codes') || []
      if (pharmacyItems.length) {
        const daysSupplySupportingInfos = [
          ...new Set(
            pharmacyItems.map(item => Number(item?.days_supply) || 1)
          )
        ].map(value => ({
          category: 'days-supply',
          code: null,
          code_text: null,
          start_date: null,
          end_date: null,
          value: String(value),
          valueUnit: 'd',
          unit: 'd',
          reason: null,
          image_content_type: null,
          image_title: null,
          image_creation_date: null,
          image_value: null,
          image_minio_path: null
        }))
        const pharmacySupportingInfo = buildSupportingInfo(
          'pharmacy',
          daysSupplySupportingInfos
        )
        const sent = await sendItemsPreAuth(
          'pharmacy',
          pharmacyItems.map(item => {
            const itemDaysSupply = String(Number(item?.days_supply) || 1)
            const matchingDaysSupplyIndex = pharmacySupportingInfo.findIndex(
              info =>
                info.category === 'days-supply' &&
                info.value === itemDaysSupply
            )
            const itemSupportingInfo = pharmacySupportingInfo
              .map((info, index) => ({ info, index: index + 1 }))
              .filter(
                ({ info, index }) =>
                  info.category !== 'days-supply' ||
                  index === matchingDaysSupplyIndex + 1
              )
              .map(({ index }) => index)
            const { days_supply, __approvalIndex, ...itemWithoutDaysSupply } =
              item
            return {
              ...itemWithoutDaysSupply,
              supporting_info: itemSupportingInfo
            }
          }),
          basePayload,
          pharmacySupportingInfo
        )
        if (sent) {
          sentAnyApproval = true
          await markItemsSent(resolvedEmrId, pharmacyItems)
        }
      }

      const payloadType = getPayloadType()
      const nonPharmacyItems =
        itemsObj?.filter(item => item?.type !== 'medication-codes') || []
      if (nonPharmacyItems.length) {
        const supportingInfo = buildSupportingInfo(payloadType)
        const sent = await sendItemsPreAuth(
          payloadType,
          nonPharmacyItems.map(item => {
            const { __approvalIndex, ...itemWithoutApprovalIndex } = item
            return {
              ...itemWithoutApprovalIndex,
              supporting_info: supportingInfo.map((_, index) => index + 1)
            }
          }),
          basePayload,
          supportingInfo
        )
        if (sent) {
          sentAnyApproval = true
          await markItemsSent(resolvedEmrId, nonPharmacyItems)
        }
      }

      if (!sentAnyApproval) {
        toast.error(t('No approval request was sent.'))
        return
      }

      const selectedIndexes = new Set(itemsObj.map(item => item.__approvalIndex))
      const updatedItems = (values?.items || []).map((item, index) =>
        selectedIndexes.has(index) ? { ...item, is_approval_sent: true } : item
      )
      setSelectedApprovalItemIndexes([])
      toast.success(t('Approval request sent successfully.'))
      await onUpdated?.(updatedItems)
      onClose?.()
    } finally {
      setIsSaving(false)
    }
  }

  const columns = [
    {
      header: (
        <input
          type='checkbox'
          className='h-4 w-4 accent-blue'
          checked={allSelected}
          onChange={toggleSelectAll}
        />
      ),
      body: rowData => (
        <input
          type='checkbox'
          className='h-4 w-4 accent-blue'
          checked={selectedApprovalItemIndexes.includes(
            rowData.__approvalIndex
          )}
          onChange={() => toggleSelectedItem(rowData.__approvalIndex)}
        />
      ),
      style: { width: '45px' }
    },
     {
      field: 'non_standard_code',
      header: t('Code'),
      body: rowData =>
        rowData?.non_standard_code || rowData?.item_code 
    },
    {
      field: 'non_standard_description',
      header: t('Item'),
      body: rowData => (
        <div className='max-w-[300px]'>
          <p>
            {rowData?.non_standard_description ||
              rowData?.item_name ||
              rowData?.non_standard_description ||
              '-'}
          </p>
        </div>
      )
    },
    {
      field: 'item_type',
      header: t('Type'),
      body: rowData =>
        rowData?.selected_category || rowData?.item_type || rowData?.type || '-'
    },
    {
      field: 'quantity',
      header: t('Qty'),
      body: rowData => rowData?.quantity || 1
    },
    {
      field: 'unit_price',
      header: t('Unit Price'),
      body: rowData => Number(rowData?.unit_price || 0).toFixed(2)
    },
    {
      field: 'approval_status',
      header: t('Approval Status'),
      body: rowData => (
        <span className='rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-700'>
          {t(getApprovalStatus(rowData) || 'pending')}
        </span>
      )
    }
  ]

  return (
    <div className='flex flex-col gap-4'>
      <div className='rounded border border-blue-100 bg-blue-50/70 p-3'>
        <div className='flex flex-col gap-2 md:flex-row md:items-center md:justify-between'>
          <div>
            <p className='text-[14px] font-bold text-blue-700'>
              {t('Pending Approval Items')}
            </p>
            <p className='text-[11px] font-medium text-gray-600'>
              {pendingApprovalItems.length
                ? t('Select pending items to send for approval.')
                : t('No pending approval items found for this EMR.')}
            </p>
          </div>
          {pendingApprovalItems.length > 0 && (
            <CustomButton
              type='button'
              className='h-[25px'
              icon='pi pi-check'
              onClick={toggleSelectAll}
            >
              {allSelected ? t('Clear Selection') : t('Select All')}
            </CustomButton>
          )}
        </div>
      </div>

      <CustomTable
        columns={columns}
        data={pendingApprovalItems}
        emptyMessage='No pending approval items found for this EMR.'
        tableStyle={{ minWidth: '950px' }}
        rootyStyles='max-h-[58vh] overflow-auto border border-gray-200 rounded border-b-0 custom-table'
      />

      <div className='flex items-center justify-end gap-2 border-t border-gray-100 pt-3'>
        <CustomButton
          label={t('Cancel')}
          buttonType='reset'
          onClick={onClose}
          disabled={isSaving}
        />
        {canUpdateEmr && <CustomButton
          type='button'
          label={t('Send Approval Request')}
          buttonType='primary'
          loading={isSaving}
          loadingText={t('Sending')}
          disabled={!selectedApprovalItemIndexes.length || isSaving}
          onClick={handleSendApproval}
        />}
      </div>
    </div>
  )
}

export default ManualApprovalItemsModal
